import React, { useEffect, useMemo, useState } from "react";
import SubHeader from "components/SubHeader";
import SubHeaderTitle from "components/SubHeader/SubHeaderTitle";
import SubHeaderBackButton from "components/SubHeader/SubHeaderBackButton";
import { useTranslation } from "react-i18next";
import { netCall } from "utils/netCall";
import { UserManagementApiUrl } from "environment/routeSettings";
import { TUser, TWaterSubscriber } from "types/dto";
import { toast } from "react-toastify";
import { useParams } from "react-router-dom";
import { omit, pick } from "lodash";
import { ConfirmationModal } from "components/ConfirmationModal";
import { TEditFormTypes, UserInfo } from "./components/UserInfo";
import { RolesInfo } from "./components/RolesInfo";
import { RolesChooserModal } from "./components/RolesChooserModal";
import { ResetPasswordModal } from "./components/ResetPasswordModal";
import { TRolesGridRow } from "./components/RolesChooserModal/RolesGrid/types";
import { TResponseCode } from "utils/netCall/types";
import { usePermissions } from "components/Permission/usePermissions";

const SubscriberDetailsView = () => {
  const { t } = useTranslation();
  const { userId } = useParams();
  const { hasPermission } = usePermissions();

  const [isGettingData, setIsGettingData] = useState<boolean>(false);
  const [initData, setInitData] = useState<TUser>();
  const [isUserInfoEnabledEditing, setIsUserInfoEnabledEditing] =
    useState(false);
  const [userInfoSubmitting, setUserInfoSubmitting] = useState(false);
  const [isRolesChooserOpen, setIsRolesChooserOpen] = useState(false);
  const [selectedNewRoles, setSelectedNewRoles] = useState<TRolesGridRow[]>();
  const [selectedRoleToDelete, setSelectedRoleToDelete] =
    useState<TRolesGridRow>();
  const [isResetPasswordModalOpen, setIsResetPasswordModalOpen] =
    useState(false);
  const [confirmationModalType, setConfirmationModalType] = useState<
    "addRoles" | "deleteRole"
  >();
  const [confirmationSubmitting, setConfirmationSubmitting] = useState(false);

  const confirmationModalDesc = useMemo(() => {
    if (confirmationModalType === "addRoles") {
      return t("confirmAddRolesDesc");
    } else if (confirmationModalType === "deleteRole") {
      return t("confirmDeleteRoleDesc");
    }
  }, [confirmationModalType]);

  const onChooseRoles = (value: TRolesGridRow[]) => {
    setSelectedNewRoles(value);
    setConfirmationModalType("addRoles");
  };

  const onDeleteRoleGridActionClick = (role: TRolesGridRow) => {
    if (!hasPermission(["superadmin", "d_user"])) {
      toast.error(t("youDontHavePermissionToEdit"));
      return;
    }
    setSelectedRoleToDelete(role);
    setConfirmationModalType("deleteRole");
  };

  const getUserDetails = async () => {
    setIsGettingData(true);
    const res = await netCall<TUser>({
      method: "GET",
      endPoint: `${UserManagementApiUrl()}/api/SamaUser/GetUserProfile/${userId}`,
    });
    if (res.responseCode === TResponseCode.OK) {
      setInitData(res.results);
    } else {
      toast.error(t("errorInGettingData"));
    }
    setIsGettingData(false);
  };

  const onSuccessfulUpdate = () => {
    toast.success(t("operationSuccessful"));
    getUserDetails();
  };
  const onFailedUpdate = () => {
    toast.error(t("operationFailed"));
  };

  const onSaveUserInfo = async (value: TEditFormTypes) => {
    setUserInfoSubmitting(true);
    const res = await netCall<TWaterSubscriber>({
      method: "POST",
      endPoint: `${UserManagementApiUrl()}/api/SamaUser/UpdateUser`,
      body: {
        ...pick(initData, ["id"]),
        ...value,
        roles: initData?.roles.map((item) => item.id),
      },
    });
    if (res.responseCode === TResponseCode.OK) {
      setUserInfoSubmitting(false);
      setIsUserInfoEnabledEditing(false);
      onSuccessfulUpdate();
    } else {
      setUserInfoSubmitting(false);
      onFailedUpdate();
    }
  };
  const submitAddRoles = async () => {
    const newRolesIds = (selectedNewRoles || []).map((item) => item.id);
    const updatedRolesIds = (initData?.roles || []).map((item) => item.id);
    const rolesToSent = [...new Set([...newRolesIds, ...updatedRolesIds])];
    setConfirmationSubmitting(true);
    const res = await netCall<any>({
      method: "POST",
      endPoint: `${UserManagementApiUrl()}/api/SamaUser/UpdateUser`,
      body: {
        ...omit(initData, ["roles"]),
        roles: rolesToSent,
      },
    });
    if (res.responseCode === TResponseCode.OK) {
      setConfirmationSubmitting(false);
      setIsRolesChooserOpen(false);
      setConfirmationModalType(undefined);
      setSelectedNewRoles(undefined);
      onSuccessfulUpdate();
    } else {
      setConfirmationSubmitting(false);
      onFailedUpdate();
    }
  };
  const submitDeleteRole = async () => {
    setConfirmationSubmitting(true);
    const res = await netCall<any>({
      method: "POST",
      endPoint: `${UserManagementApiUrl()}/api/SamaUser/UpdateUser`,
      body: {
        ...omit(initData, ["roles"]),
        roles: initData?.roles
          .filter((role) => role.id !== selectedRoleToDelete?.id)
          .map((item) => item.id),
      },
    });
    if (res.responseCode === TResponseCode.OK) {
      setConfirmationSubmitting(false);
      setConfirmationModalType(undefined);
      setSelectedRoleToDelete(undefined);
      onSuccessfulUpdate();
    } else {
      setConfirmationSubmitting(false);
      onFailedUpdate();
    }
  };

  const onSubmitConfirmation = () => {
    if (confirmationModalType === "addRoles") {
      submitAddRoles();
    } else if (confirmationModalType === "deleteRole") {
      submitDeleteRole();
    }
  };

  useEffect(() => {
    if (userId) {
      getUserDetails();
    }
  }, [userId]);

  return (
    <>
      <SubHeader>
        <SubHeaderTitle>
          <SubHeaderBackButton />
          {t("userDetails")}
        </SubHeaderTitle>
      </SubHeader>
      <div
        className={"flex flex-col grid-cols-3 gap-y-4 p-4 h-full overflow-auto"}
      >
        <div className="col-span-3">
          <UserInfo
            data={initData}
            isLoading={isGettingData}
            onSaveClick={onSaveUserInfo}
            isButtonLoading={userInfoSubmitting}
            enableEditing={isUserInfoEnabledEditing}
            onEditClick={() => setIsUserInfoEnabledEditing(true)}
            onResetPasswordClick={() => {
              setIsResetPasswordModalOpen(true);
            }}
          />
        </div>
        <div className="col-span-3">
          <RolesInfo
            assignedRoles={initData?.roles}
            isLoading={isGettingData}
            onAddRoleClick={() => setIsRolesChooserOpen(true)}
            onDeleteRoleGridActionClick={onDeleteRoleGridActionClick}
          />
        </div>
      </div>
      {isRolesChooserOpen && (
        <RolesChooserModal
          onClose={() => {
            setIsRolesChooserOpen(false);
          }}
          isOpen={isRolesChooserOpen}
          onSubmitSelectedRoles={onChooseRoles}
        />
      )}
      {isResetPasswordModalOpen && (
        <ResetPasswordModal
          userId={userId}
          onClose={() => setIsResetPasswordModalOpen(false)}
          onSuccess={() => {
            setIsResetPasswordModalOpen(false);
            getUserDetails();
          }}
          isOpen={isResetPasswordModalOpen}
        />
      )}
      {confirmationModalType && (
        <ConfirmationModal
          isOpen={!!confirmationModalType}
          onSubmit={onSubmitConfirmation}
          onClose={() => setConfirmationModalType(undefined)}
          isLoading={confirmationSubmitting}
          title={t("acceptance")}
          description={confirmationModalDesc}
          type={confirmationModalType === "deleteRole" ? "warning" : "info"}
        />
      )}
    </>
  );
};

export default SubscriberDetailsView;
